import { FC } from "react";
import dayjs from "dayjs";
import cx from "classnames";
import { ClickableCard } from "@/components/Card";
import { IconButton } from "@/components/Button";
import {
  BinIcon,
  DragIcon,
  LocationIcon,
  BuildingIcon,
  InfoIcon,
} from "@/components/Icon";
import Typography from "@/components/Typography";
import { useResponsive, useSnackbar, useDialog } from "@/hooks";
import { JobType, JOB_STATUS } from "@/fetch/suppliers";
import styles from "./InterestedJobCard.module.scss";

type Props = {
  job: JobType;
  provided?: any;
  onDelete: (job: JobType) => void;
  isLoading: boolean;
};

const InterestedJobCard: FC<Props> = ({
  job,
  provided,
  onDelete,
  isLoading,
}) => {
  const { isMobile } = useResponsive();
  const { openDialog } = useDialog("job");

  const interestedDate = dayjs((job?.interested_at || 0) * 1000);

  const tenMinutesAgoDate = dayjs().subtract(10, "minute");
  const canGetDeleted = interestedDate.isSameOrAfter(tenMinutesAgoDate);

  const replacingRelativeTimes = {
    "1 Month": "1 month",
    Months: "months",
    "1m": "1 minute",
    m: " minutes",
    "1h": "1 hour",
    h: " hours",
    "1d": "1 day",
    d: " days",
    "1y": "1 year",
    y: " years",
  };

  const interestedDateFromNow = `${interestedDate.fromNow()}`;

  const relatedRelativeTime = Object.entries(replacingRelativeTimes).find(
    ([key, value]) => interestedDateFromNow.indexOf(key) > -1
  );

  const showingInterestedDate = interestedDateFromNow.replace(
    relatedRelativeTime?.[0] || "",
    relatedRelativeTime?.[1] || ""
  );

  const isActive = job.status === JOB_STATUS.Active;
  const isPlaced = job.status === JOB_STATUS.Placed;

  return (
    <div className={styles.root}>
      <ClickableCard
        className={cx(styles.card, { [styles.disabledCard]: !isActive })}
        onClick={() => {
          if (!isActive) return;
          openDialog({ id: job?.id });
        }}
        outlined={!isActive}
      >
        {!isActive && (
          <div className={styles.statusContainer}>
            <InfoIcon className={styles.infoIcon} />
            <Typography color="error.main" variant="body2">
              {isPlaced
                ? "This job has been filled"
                : "Job no longer available"}
            </Typography>
          </div>
        )}
        <div className={styles.titleContainer}>
          <Typography
            variant="h6"
            className={styles.title}
            color={isActive ? "text.primary" : "text.secondary"}
          >
            {job?.role}
          </Typography>
          {Boolean(job?.sub_role) && (
            <Typography
              variant="subtitle1"
              className={styles.subtitle}
              color="text.secondary"
            >
              {isMobile ? job?.sub_role : <>&nbsp;&nbsp;{job?.sub_role}</>}
            </Typography>
          )}
        </div>
        <div className={styles.bottomContainer}>
          <div className={styles.detailContainer}>
            <LocationIcon className={styles.detailIcon} />
            <Typography
              variant="body2"
              className={styles.detailText}
              color={isActive ? "text.primary" : "text.secondary"}
            >
              {job?.city}
            </Typography>
          </div>
          <div className={styles.detailContainer}>
            <BuildingIcon className={styles.detailIcon} />
            <Typography
              variant="body2"
              className={styles.detailText}
              color={isActive ? "text.primary" : "text.secondary"}
            >
              {job?.partner_name}
            </Typography>
          </div>
          <Typography
            variant="body2"
            color="text.secondary"
            className={styles.interestedTime}
          >
            Interested,&nbsp;
            {showingInterestedDate === "now ago"
              ? "now"
              : showingInterestedDate}
          </Typography>
        </div>
      </ClickableCard>
      {canGetDeleted && (
        <IconButton disabled={isLoading} onClick={() => onDelete(job)}>
          <BinIcon variant="outlined" className={styles.actionIcon} />
        </IconButton>
      )}
      <IconButton disabled={isLoading} {...provided.dragHandleProps}>
        <DragIcon className={styles.actionIcon} />
      </IconButton>
    </div>
  );
};

export default InterestedJobCard;
